import { useEffect, useRef, useState } from 'react'
import { pdfjsLib, PDFDocumentProxy } from '../lib/pdfjs'
import { PdfPage } from './PdfPage'
import { SignatureFieldType } from '../services/signService'

export interface PlacedField {
  id: string // client-only, for React keys/deletion — never sent to the backend
  signerIndex: number
  type: SignatureFieldType
  page: number // 0-based
  xPct: number
  yPct: number
  widthPct: number
  heightPct: number
}

// Cycles rather than growing with signer count — plenty distinguishable for
// the handful of co-signers a conveyancing matter realistically has (buyer/
// seller, +/- a guarantor).
const SIGNER_COLORS = ['#0F2742', '#1FA7A1', '#C2410C', '#7C3AED', '#B91C1C']
export const signerColor = (index: number) => SIGNER_COLORS[index % SIGNER_COLORS.length]

const FIELD_LABELS: Record<SignatureFieldType, string> = {
  signature: 'Signature',
  initial: 'Initials',
  date: 'Date',
  text: 'Text',
  paragraph: 'Paragraph',
}

// Standard starting size per field type, as a percentage of the page's own
// width/height — not a fixed pixel size, so a field starts out the same
// proportion of the page regardless of the document's actual page size or
// the current zoom level (this canvas's pixel size changes with zoom; the
// underlying PDF page size never does). The tenant resizes/moves from here
// rather than drawing a box freehand.
const STANDARD_SIZE_PCT: Record<SignatureFieldType, { widthPct: number; heightPct: number }> = {
  signature: { widthPct: 0.28, heightPct: 0.06 },
  // Sized (and proportioned) to match SignatureCapture.tsx's narrower
  // initials capture canvas — the old box was both oversized and a much
  // wider aspect ratio than the captured image, so a 1-2 letter mark ended
  // up small and adrift in a lot of surrounding white space.
  initial: { widthPct: 0.07, heightPct: 0.04 },
  date: { widthPct: 0.19, heightPct: 0.045 },
  text: { widthPct: 0.25, heightPct: 0.045 },
  paragraph: { widthPct: 0.4, heightPct: 0.13 },
}

const MIN_FIELD_PX = { width: 40, height: 22 }
const BASE_PAGE_WIDTH = 640
const MIN_ZOOM = 0.5
const MAX_ZOOM = 2
const ZOOM_STEP = 0.25

// First + last initial (e.g. "Jane Smith" -> "JS") — used for initial
// fields, since a full name would be redundant with what's actually going
// on the page there. Falls back to a single letter for a one-word name.
const initialsFor = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0][0].toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

// "Signature (Jane Smith)" / "Initial (JS)" / "Date (Jane Smith)" — shown
// inside a placed field's box and its hover preview.
const fieldDisplayLabel = (type: SignatureFieldType, signerName: string): string => {
  const typeLabel = type === 'initial' ? 'Initial' : FIELD_LABELS[type]
  const representation = type === 'initial' ? initialsFor(signerName) : signerName
  return `${typeLabel} (${representation})`
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

interface FieldBoxProps {
  field: PlacedField
  signerName: string
  size: { width: number; height: number }
  onChange: (patch: Partial<Pick<PlacedField, 'xPct' | 'yPct' | 'widthPct' | 'heightPct'>>) => void
  onRemove: () => void
}

// A placed field, draggable to move and with a bottom-right handle to
// resize — both convert back to page-relative percentages on every change
// (see PlacedField's xPct/yPct/widthPct/heightPct) so they survive the gap
// between this canvas's pixel size and the PDF's own page size.
const FieldBox: React.FC<FieldBoxProps> = ({ field, signerName, size, onChange, onRemove }) => {
  const dragRef = useRef<{ mode: 'move' | 'resize'; startX: number; startY: number; origX: number; origY: number; origW: number; origH: number } | null>(null)

  const pxLeft = field.xPct * size.width
  const pxTop = field.yPct * size.height
  const pxWidth = field.widthPct * size.width
  const pxHeight = field.heightPct * size.height

  const startDrag = (mode: 'move' | 'resize') => (e: React.PointerEvent) => {
    e.stopPropagation()
    ;(e.target as Element).setPointerCapture(e.pointerId)
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, origX: pxLeft, origY: pxTop, origW: pxWidth, origH: pxHeight }
  }

  const handleMove = (e: React.PointerEvent) => {
    const drag = dragRef.current
    if (!drag) return
    const dx = e.clientX - drag.startX
    const dy = e.clientY - drag.startY

    if (drag.mode === 'move') {
      const x = clamp(drag.origX + dx, 0, size.width - drag.origW)
      const y = clamp(drag.origY + dy, 0, size.height - drag.origH)
      onChange({ xPct: x / size.width, yPct: y / size.height })
    } else {
      const width = clamp(drag.origW + dx, MIN_FIELD_PX.width, size.width - drag.origX)
      const height = clamp(drag.origH + dy, MIN_FIELD_PX.height, size.height - drag.origY)
      onChange({ widthPct: width / size.width, heightPct: height / size.height })
    }
  }

  const endDrag = () => {
    dragRef.current = null
  }

  return (
    <div
      className="absolute flex items-center justify-between gap-1 px-1 text-[10px] font-medium text-white rounded-sm cursor-move select-none touch-none"
      style={{ left: pxLeft, top: pxTop, width: pxWidth, height: pxHeight, backgroundColor: `${signerColor(field.signerIndex)}CC` }}
      onPointerDown={startDrag('move')}
      onPointerMove={handleMove}
      onPointerUp={endDrag}
      onClick={(e) => e.stopPropagation()}
    >
      <span className="truncate pointer-events-none">{fieldDisplayLabel(field.type, signerName)}</span>
      <button
        type="button"
        className="shrink-0 hover:opacity-70"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove() }}
        aria-label="Remove field"
      >
        ×
      </button>
      <div
        className="absolute -right-1 -bottom-1 w-3 h-3 bg-white border border-gray-500 rounded-sm cursor-se-resize touch-none"
        onPointerDown={startDrag('resize')}
      />
    </div>
  )
}

interface SignatureFieldPlacerProps {
  documentUrl: string
  signerNames: string[]
  activeSignerIndex: number
  fieldType: SignatureFieldType
  fields: PlacedField[]
  onFieldsChange: (fields: PlacedField[]) => void
}

// Tenant-side field placement: renders every page of the source PDF via
// pdfjs-dist, zoomable independently of the field percentages themselves
// (see renderWidth below). Clicking a page drops a standard-size field (see
// STANDARD_SIZE_PCT) for the currently-selected signer/field type, which can
// then be dragged to move and resized from its bottom-right handle, or
// removed.
export const SignatureFieldPlacer: React.FC<SignatureFieldPlacerProps> = ({
  documentUrl,
  signerNames,
  activeSignerIndex,
  fieldType,
  fields,
  onFieldsChange,
}) => {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [hoverPos, setHoverPos] = useState<{ page: number; x: number; y: number } | null>(null)

  useEffect(() => {
    let cancelled = false
    pdfjsLib.getDocument({ url: documentUrl }).promise
      .then((doc) => { if (!cancelled) setPdf(doc) })
      .catch(() => { if (!cancelled) setError('Failed to load document preview') })
    return () => { cancelled = true }
  }, [documentUrl])

  // Shared by the hover preview and the actual click-to-place, so what you
  // see under the cursor is exactly what you get.
  const boxForCursor = (cursorX: number, cursorY: number, size: { width: number; height: number }) => {
    const standard = STANDARD_SIZE_PCT[fieldType]
    const width = Math.min(standard.widthPct * size.width, size.width - 4)
    const height = Math.min(standard.heightPct * size.height, size.height - 4)
    const x = clamp(cursorX - width / 2, 0, size.width - width)
    const y = clamp(cursorY - height / 2, 0, size.height - height)
    return { x, y, width, height }
  }

  const handlePointerMoveOverPage = (page: number) => (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setHoverPos({ page, x: e.clientX - rect.left, y: e.clientY - rect.top })
  }

  const placeField = (page: number, size: { width: number; height: number }) => (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const box = boxForCursor(e.clientX - rect.left, e.clientY - rect.top, size)

    onFieldsChange([
      ...fields,
      {
        id: `field-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        signerIndex: activeSignerIndex,
        type: fieldType,
        page,
        xPct: box.x / size.width,
        yPct: box.y / size.height,
        widthPct: box.width / size.width,
        heightPct: box.height / size.height,
      },
    ])
  }

  const updateField = (id: string, patch: Partial<Pick<PlacedField, 'xPct' | 'yPct' | 'widthPct' | 'heightPct'>>) => {
    onFieldsChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)))
  }

  const removeField = (id: string) => onFieldsChange(fields.filter((f) => f.id !== id))

  if (error) return <p className="text-sm text-red-600">{error}</p>
  if (!pdf) return <p className="text-sm text-gray-500">Loading document preview...</p>

  const pageNumbers = Array.from({ length: pdf.numPages }, (_, i) => i + 1)

  const renderWidth = Math.round(BASE_PAGE_WIDTH * zoom)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-gray-500">
          Click on the document to place a <strong>{FIELD_LABELS[fieldType]}</strong> field for{' '}
          <strong style={{ color: signerColor(activeSignerIndex) }}>{signerNames[activeSignerIndex]}</strong> — drag to move, drag the corner to resize.
        </p>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setZoom((z) => clamp(Math.round((z - ZOOM_STEP) * 100) / 100, MIN_ZOOM, MAX_ZOOM))}
            disabled={zoom <= MIN_ZOOM}
            aria-label="Zoom out"
            className="w-7 h-7 flex items-center justify-center text-sm font-medium border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white"
          >
            −
          </button>
          <span className="w-12 text-center text-xs text-gray-600">{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            onClick={() => setZoom((z) => clamp(Math.round((z + ZOOM_STEP) * 100) / 100, MIN_ZOOM, MAX_ZOOM))}
            disabled={zoom >= MAX_ZOOM}
            aria-label="Zoom in"
            className="w-7 h-7 flex items-center justify-center text-sm font-medium border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-white"
          >
            +
          </button>
        </div>
      </div>
      <div className="space-y-4 max-h-[60vh] overflow-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
        {pageNumbers.map((pageNumber) => {
          const pageIndex = pageNumber - 1
          return (
            <PdfPage key={pageNumber} pdf={pdf} pageNumber={pageNumber} width={renderWidth}>
              {(size) => (
                <div
                  className="absolute inset-0 cursor-default"
                  onClick={placeField(pageIndex, size)}
                  onMouseMove={handlePointerMoveOverPage(pageIndex)}
                  onMouseLeave={() => setHoverPos((h) => (h?.page === pageIndex ? null : h))}
                >
                  {hoverPos && hoverPos.page === pageIndex && (() => {
                    const box = boxForCursor(hoverPos.x, hoverPos.y, size)
                    return (
                      <div
                        className="absolute border-2 border-dashed rounded-sm flex items-center justify-center text-[10px] font-medium pointer-events-none"
                        style={{
                          left: box.x,
                          top: box.y,
                          width: box.width,
                          height: box.height,
                          borderColor: signerColor(activeSignerIndex),
                          backgroundColor: `${signerColor(activeSignerIndex)}22`,
                          color: signerColor(activeSignerIndex),
                        }}
                      >
                        {fieldDisplayLabel(fieldType, signerNames[activeSignerIndex])}
                      </div>
                    )
                  })()}

                  {fields.filter((f) => f.page === pageIndex).map((f) => (
                    <FieldBox
                      key={f.id}
                      field={f}
                      signerName={signerNames[f.signerIndex] || `Signer ${f.signerIndex + 1}`}
                      size={size}
                      onChange={(patch) => updateField(f.id, patch)}
                      onRemove={() => removeField(f.id)}
                    />
                  ))}
                </div>
              )}
            </PdfPage>
          )
        })}
      </div>
    </div>
  )
}
